import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMenu, FiX } from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import api from '../utils/api.js';

// Configure axios with retry logic for health check
axiosRetry(axios, {
  retries: 3, // Retry up to 3 times
  retryDelay: (retryCount) => retryCount * 1000, // 1s delay between retries
  retryCondition: (error) => true, // Retry on any error
});

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme } = useTheme();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [availability, setAvailability] = useState({ status: 'Not Available', message: '' });
  const [isServerAwake, setIsServerAwake] = useState(false);
  const [loading, setLoading] = useState(true);

  // Define backend URL with a fallback
  const BACKEND_URL = 'https://minimum-karrah-granday-b79700a0.koyeb.app';

  useEffect(() => {
    // Log backend URL for debugging
    console.log('Backend URL:', BACKEND_URL);

    const checkServerHealth = async () => {
      try {
        if (!BACKEND_URL || BACKEND_URL.includes('undefined')) {
          throw new Error('Backend URL is not defined. Please set REACT_APP_BACKEND_URL in Vercel.');
        }
        const response = await axios.get(`${BACKEND_URL}/api/health`);
        if (response.status === 200 && response.data.status === 'OK') {
          setIsServerAwake(true);
        }
      } catch (error) {
        console.error('Server wake-up error:', error);
        setAvailability({ status: 'Not Available', message: 'Failed to connect to server' });
        setLoading(false);
      }
    };

    const fetchAvailability = async () => {
      try {
        const res = await api.get('/api/availability');
        setAvailability(res.data);
      } catch (error) {
        console.error('Error fetching availability:', error);
        setAvailability({ status: 'Not Available', message: 'Error fetching availability' });
      } finally {
        setLoading(false);
      }
    };

    // First check server health, then fetch availability if awake
    checkServerHealth().then(() => {
      if (isServerAwake) {
        fetchAvailability();
      }
    });
  }, [isServerAwake, BACKEND_URL]);

  const navItems = [
    { name: 'Home', path: '/home' },
    { name: 'Projects', path: '/projects' },
    { name: 'Blog', path: '/blog' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  const getStatusColor = () => {
    if (loading || !isServerAwake) return 'bg-dark-700';
    switch (availability.status) {
      case 'Open to Work':
        return 'bg-green-500';
      case 'Available for Freelance':
        return 'bg-blue-500';
      default:
        return 'bg-red-500';
    }
  };

  const AvailabilityIndicator = () => (
    <div className="flex items-center space-x-2">
      <span className={`wave-dot ${getStatusColor()}`}></span>
      <span className="text-xs text-light-200">
        {loading || !isServerAwake ? 'Loading...' : availability.status}
      </span>
    </div>
  );

  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: "circOut" }}
      className="fixed top-6 left-0 right-0 z-50 glass-effect max-w-5xl w-[92%] mx-auto rounded-2xl shadow-2xl border border-white/5 dark:border-white/10"
    >
      <div className="px-6 py-1">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link to="/home" className="group flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-secondary_accent flex items-center justify-center text-dark-900 font-black text-xl group-hover:rotate-12 transition-transform duration-300">
              G
            </div>
            <span className="text-xl font-bold tracking-tight text-white group-hover:text-secondary_accent transition-colors font-clash">
              Granday
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`relative py-2 text-[13px] font-medium tracking-wide uppercase transition-all duration-300 ${
                  isActive(item.path)
                    ? 'text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {item.name}
                {isActive(item.path) && (
                  <motion.div
                    layoutId="nav-active"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-secondary_accent rounded-full"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
            ))}
          </div>

          {/* Theme Toggle, Availability & User Menu */}
          <div className="hidden md:flex items-center gap-6">
            <div className="px-3 py-1 rounded-full bg-white/5 border border-white/5 flex items-center">
              <AvailabilityIndicator />
            </div>
            
            <div className="h-4 w-px bg-white/10" />

            {user ? (
              <div className="flex items-center gap-2">
                <Link to="/admin" className="text-[11px] font-bold uppercase tracking-widest text-gray-400 hover:text-white transition-colors">
                  Dashboard
                </Link>
                <button 
                  onClick={logout}
                  className="px-4 py-1.5 rounded-lg bg-white/5 hover:bg-red-500/10 text-white border border-white/10 hover:border-red-500/20 text-[11px] font-bold uppercase tracking-widest transition-all duration-300"
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <Link 
                to="/login" 
                className="px-5 py-2 rounded-xl bg-secondary_accent hover:bg-white text-dark-900 text-[11px] font-bold uppercase tracking-widest transition-all duration-300 shadow-lg shadow-secondary_accent/10"
              >
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-gray-400 hover:text-white transition-colors"
          >
            {isOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden py-6 border-t border-white/5 space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={`py-3 px-4 rounded-xl text-sm font-medium transition-all ${
                    isActive(item.path)
                      ? 'bg-secondary_accent text-dark-900'
                      : 'bg-white/5 text-gray-400'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
            
            <div className="pt-4 border-t border-white/5 flex items-center justify-between">
              <AvailabilityIndicator />
              {user ? (
                <div className="flex gap-2">
                  <Link to="/admin" onClick={() => setIsOpen(false)} className="text-xs font-bold uppercase text-gray-400">Admin</Link>
                  <button onClick={() => { logout(); setIsOpen(false); }} className="text-xs font-bold uppercase text-red-400">Logout</button>
                </div>
              ) : (
                <Link to="/login" onClick={() => setIsOpen(false)} className="px-4 py-2 rounded-lg bg-secondary_accent text-dark-900 text-[10px] font-bold uppercase tracking-widest">Login</Link>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

export default Navbar;